import React, { useMemo } from 'react';
import { BuslineQueryData } from '@area-restrita/api-client/model/buslineQueryData';
import { useGetBuslinesInfinite } from '@area-restrita/api-client/endpoints/buslines/buslines.gen';
import { AlertTriangle } from 'lucide-react';

import QueryCombobox, {
  QueryComboboxImplProps,
  QueryComboboxOption,
} from '@/components/QueryCombobox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDebouncedState } from '@/hooks/useDebounce';

export type SelectedBusline = {
  id: number;
  name: string;
  code: string;
  companyId: number;
};

type BuslineOption = QueryComboboxOption & {
  companyId?: number;
  hasPermissionWarning?: boolean;
};

const toQueryComboboxOption = (busline: BuslineQueryData): BuslineOption => ({
  label: `${busline.code} - ${busline.name}`,
  value: busline.id,
  companyId: busline.company.id,
});

const toSelectedBuslineOption = (
  busline: SelectedBusline,
  companyIds?: number[],
): BuslineOption => ({
  label: `${busline.code} - ${busline.name}`,
  value: busline.id,
  companyId: busline.companyId,
  hasPermissionWarning:
    companyIds !== undefined && !companyIds.includes(busline.companyId),
});

const renderBuslineOption = (
  option: BuslineOption,
  permissionWarningMessage: string,
) => (
  <div className="flex min-w-0 flex-1 items-center gap-2">
    <span className="truncate">{option.label}</span>
    {option.hasPermissionWarning && (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          </TooltipTrigger>
          <TooltipContent>{permissionWarningMessage}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
  </div>
);

type IBuslineSelectorProps = QueryComboboxImplProps<BuslineOption> & {
  companyIds?: number[];
  selectedBuslines?: SelectedBusline[];

  permissionWarningMessage?: string;
};

const BuslineSelector: React.FC<IBuslineSelectorProps> = ({
  companyIds,
  selectedBuslines = [],
  permissionWarningMessage = 'Esta rota não está associada a nenhuma empresa selecionada e deve ser removida.',
  ...props
}) => {
  const [searchValue, setSearchValue, rawSearchValue] =
    useDebouncedState<string>('');
  const shouldFetch = companyIds === undefined || companyIds.length > 0;

  const query = useGetBuslinesInfinite(
    {
      limit: 100,
      page: 1,
      search: searchValue,
      companyIds: companyIds?.length ? companyIds : undefined,
    },
    {
      query: {
        enabled: shouldFetch,
        queryKeyHashFn: (args) => JSON.stringify(['infinite', ...args]),
        getNextPageParam: (lastPage, _, lastPageParam) =>
          lastPage.data.length > 0 ? lastPageParam + 1 : null,
      },
    },
  );

  const buslines = useMemo(() => {
    const options = new Map<string | number, BuslineOption>();

    selectedBuslines
      .map((busline) => toSelectedBuslineOption(busline, companyIds))
      .forEach((option) => options.set(option.value, option));

    query.data?.pages
      .flatMap<BuslineOption>((page) => page.data.map(toQueryComboboxOption))
      .forEach((option) => options.set(option.value, option));

    return Array.from(options.values());
  }, [companyIds, query.data, selectedBuslines]);

  return (
    <QueryCombobox
      options={buslines}
      isLoading={shouldFetch && query.isFetching}
      searchValue={rawSearchValue}
      onSearchValueChange={setSearchValue}
      placeholder="Selecione linhas"
      searchPlaceholder="Pesquisar linha"
      emptyText="Nenhuma linha encontrada"
      plural="linhas"
      renderOption={(option) =>
        renderBuslineOption(option, permissionWarningMessage)
      }
      {...props}
    />
  );
};

export default BuslineSelector;
